import {
  ArrowRight,
  Globe2,
  House,
  ListFilter,
  LockKeyhole,
  MonitorSmartphone,
  Settings2,
  Signal,
  Zap,
} from "lucide-react";
import { Link } from "react-router-dom";
import { supportedServices } from "@yunliu/shared";
import { LeadForm } from "../components/LeadForm";
import { ProductShowcase, SmartRoutingComparison } from "../components/ProductShowcase";
import { BrandAppIcon } from "../components/icons/BrandIcons";
import { faqItems } from "../data/site";
import { useI18n } from "../i18n";

const reasons = [
  {
    icon: Zap,
    title: "上手简单",
    description: "下载安装、登录账户、选择场景。线路匹配和连接维护交给云流。",
  },
  {
    icon: Globe2,
    title: "分流精准",
    description: "只处理需要回国访问的流量，让海外网站和日常工作保持原有路径。",
  },
  {
    icon: MonitorSmartphone,
    title: "设备全面",
    description: "规划覆盖 Windows、macOS、iOS 与 Android，一个账户同步常用设备。",
  },
] as const;

export function HomePage() {
  const { t } = useI18n();
  return (
    <>
      <section className="nb-hero">
        <div className="nb-hero-glow" aria-hidden="true" />
        <div className="container nb-hero-inner">
          <div className="nb-hero-copy">
            <h1>{t("云流跨山海")}<br /><span>{t("一键连所爱")}</span></h1>
            <p>
              {t("国内影音、音乐、国服游戏，一次连接。智能分流只加速需要回国的流量，不打扰你的海外网络。")}
            </p>
            <div className="nb-hero-actions">
              <Link className="button button-large" to="/account">{t("免费体验")} <ArrowRight /></Link>
              <Link className="button button-large button-secondary" to="/pricing">{t("查看套餐")}</Link>
            </div>
          </div>
          <div className="nb-hero-product" aria-label={t("云流桌面客户端界面预览")}>
            <div className="nb-hero-product-top">
              <span><i />{t("云流客户端")}</span>
              <small>{t("客户端界面预览")}</small>
              <div className="nb-hero-window-controls" aria-hidden="true"><i /><i /><i /></div>
            </div>
            <div className="nb-hero-product-body">
              <aside className="nb-hero-product-sidebar" aria-label={t("客户端功能导航")}>
                <span className="active"><House /><small>{t("总览")}</small></span>
                <span><ListFilter /><small>{t("分流")}</small></span>
                <span><MonitorSmartphone /><small>{t("设备")}</small></span>
                <span><Settings2 /><small>{t("设置")}</small></span>
              </aside>
              <div className="nb-hero-dashboard">
                <div className="nb-hero-dashboard-head">
                  <div><small>{t("连接总览")}</small><h2>{t("晚上好，准备开始加速")}</h2></div>
                  <span className="nb-dashboard-mode"><Zap />{t("智能模式")}</span>
                </div>
                <div className="nb-hero-dashboard-grid">
                  <div className="nb-hero-orbit" aria-hidden="true">
                    <span className="nb-orbit-ring ring-one" />
                    <span className="nb-orbit-ring ring-two" />
                    <span className="nb-orbit-node node-one" />
                    <span className="nb-orbit-node node-two" />
                    <span className="nb-orbit-node node-three" />
                    <div className="nb-orbit-core"><Zap /><strong>{t("已加速")}</strong><span>{t("智能线路 · 中国")}</span></div>
                  </div>
                  <div className="nb-hero-status-panel">
                    <div><span><Signal />{t("连接状态")}</span><strong className="online"><i />{t("连接正常")}</strong></div>
                    <div><span><ListFilter />{t("当前模式")}</span><strong>{t("智能分流")}</strong><small>{t("仅处理国内应用")}</small></div>
                    <div><span><Globe2 />{t("加速线路")}</span><strong>{t("自动选择")}</strong><small>{t("按网络状态匹配")}</small></div>
                  </div>
                </div>
                <div className="nb-hero-product-bottom">
                  <div><span>{t("腾讯视频")}</span><strong>{t("影音线路")}</strong></div>
                  <div><span>{t("网易云音乐")}</span><strong>{t("音乐线路")}</strong></div>
                  <div><span>{t("海外网站")}</span><strong className="direct">{t("本地直连")}</strong></div>
                </div>
              </div>
            </div>
          </div>
        <div className="nb-hero-facts" aria-label={t("产品特性")}>
            <div><strong>{t("影音 · 音乐 · 游戏")}</strong><span>{t("覆盖常用回国场景")}</span></div>
            <div><strong>{t("智能分流")}</strong><span>{t("海外应用不绕路")}</span></div>
            <div><strong>{t("多平台客户端")}</strong><span>{t("一个账户连接常用设备")}</span></div>
          </div>
        </div>
      </section>

      <section className="nb-service-strip" aria-labelledby="services-heading">
        <div className="container">
          <p id="services-heading">{t("熟悉的国内内容，在海外继续使用")}</p>
          <div className="nb-service-list">
            {supportedServices.map((service) => (
              <div className="nb-service-logo" key={service.id}>
                <BrandAppIcon serviceId={service.id} size={28} />
                <span>{t(service.name)}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <ProductShowcase />
      <SmartRoutingComparison />

      <section className="nb-reasons section" aria-labelledby="reasons-heading">
        <div className="container">
          <div className="nb-section-heading">
            <h2 id="reasons-heading">{t("把复杂的网络选择，留在产品背后")}</h2>
          </div>
          <div className="nb-reason-grid">
            {reasons.map(({ icon: ReasonIcon, title, description }, index) => (
              <article key={title}>
                <div className="nb-reason-index">0{index + 1}</div>
                <ReasonIcon />
                <h3>{t(title)}</h3>
                <p>{t(description)}</p>
              </article>
            ))}
          </div>
          <div className="nb-proof-bar">
            <div><LockKeyhole /><span><strong>{t("安全连接")}</strong><small>{t("账户与会话保护")}</small></span></div>
            <div><MonitorSmartphone /><span><strong>{t("四端规划")}</strong><small>{t("桌面与移动平台")}</small></span></div>
            <div><Globe2 /><span><strong>{t("智能线路")}</strong><small>{t("按场景自动匹配")}</small></span></div>
          </div>
        </div>
      </section>

      <section className="section nb-faq" aria-labelledby="faq-heading">
        <div className="container faq-grid">
          <div className="faq-intro">
            <h2 id="faq-heading">{t("了解云流加速器")}</h2>
            <p>{t("产品定位、支持设备、智能分流与套餐使用方式，都在这里。")}</p>
            <a href="mailto:hello@example.com">{t("还有问题？联系支持")}</a>
          </div>
          <div className="faq-list">
            {faqItems.map((item, index) => (
              <details key={item.question} open={index === 0}>
                <summary>{t(item.question)}<span>+</span></summary>
                <p>{t(item.answer)}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      <section className="section final-cta nb-final-cta">
        <div className="container cta-panel">
          <div>
            <h2>{t("把熟悉的内容，重新连接起来")}</h2>
            <p>{t("注册即享免费体验额度，随时连接熟悉的国内影音与游戏。")}</p>
          </div>
          <LeadForm />
        </div>
      </section>
    </>
  );
}
